import {
  dispatchAccountDiscoveryNoSAMLEvent,
  dispatchAccountDiscoveryFoundSAMLEvent,
  dispatchAccountDiscoveryNoAccountEvent,
} from '../avro';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

import { ID } from '../types';
import { LOCAL_STORAGE_ACCOUNT_ID } from '../constants';
import { fetch } from './fetch';

type ResponseAccount = {
  has_saml_sso: boolean;
  id: ID;
  sso_url: string | null;
};

export type DiscoveredAccount = {
  hasSamlSso: boolean;
  id: ID;
  ssoUrl: string | null;
};

export type DiscoveryResult = {
  account?: ResponseAccount;
  errors?: string[];
};

/**
 * Attempts to discover the last account id that the user
 * logged in to
 */
function getStoredAccountId() {
  // TODO: first try useCurrentAccountId to see if the cookie
  // is available

  // Try to read stored currentAccount id from localStorage since
  // that will contain the id of the last account the user logged in to
  let storedAccountId = '';

  try {
    storedAccountId = localStorage.getItem(LOCAL_STORAGE_ACCOUNT_ID) || '';
  } catch (e) {
    // This is only used for backwards compatibility reasons, so if we cannot store it in LocalStorage for whatever reason, we can just ignore it.
  }

  const rawAccountId = Number.parseInt(storedAccountId);

  return rawAccountId && Number.isInteger(rawAccountId) && rawAccountId > 0
    ? rawAccountId
    : null;
}

/**
 * Discovers which account an email address is associated with.
 * Uses most recently logged in account id (if available) with
 * a fallback to the default account to decide which account
 * the user wants to log in to
 */
export async function discoverAccount({
  attemptStoredAccount,
  email,
}: {
  attemptStoredAccount: boolean;
  email: string;
}) {
  // Try to find the last account that the user logged in as
  const storedAccountId = attemptStoredAccount
    ? getStoredAccountId()
    : undefined;
  let account;

  // Use account discovery to see if the user is trying to log into
  // an account that is SAML enabled
  const idpData = await fetch<DiscoveryResult>(
    addTruthyParamsToUrl(urls.ACCOUNT_DISCOVERY_URL, {
      account_id: storedAccountId,
      email,
    })
  );
  account = idpData.body.account;

  if (!account && storedAccountId) {
    // This means the email and storedAccountId didn't match
    // so try making the request again w/o the storedAccountId
    // so we can just let the email dictate which account we are
    // logging in as
    const noAccountData = await fetch<DiscoveryResult>(
      addTruthyParamsToUrl(urls.ACCOUNT_DISCOVERY_URL, {
        email,
      })
    );
    account = noAccountData.body.account;
  }

  if (!account) {
    dispatchAccountDiscoveryNoAccountEvent();
  }

  if (account && !account.has_saml_sso) {
    dispatchAccountDiscoveryNoSAMLEvent();
  }

  if (account && account.has_saml_sso) {
    dispatchAccountDiscoveryFoundSAMLEvent();
  }

  return account
    ? {
        hasSamlSso: account.has_saml_sso,
        id: account.id,
        ssoUrl: account.sso_url,
      }
    : account;
}
